import { FC, SyntheticEvent, useState } from 'react';
import Dialog from 'components/common/Dialog/Dialog';
import Input from 'components/common/Input/Input';
import Link from 'components/common/Link/Link';
import '../styles.scss';
import ELEMENT from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';

type CustomParserDialogProps = {
  newParserDialogIsOpen: boolean;
  setNewParserDialogIsOpen: (isOpen: boolean) => void;
  onModifyParser: (parser: string) => void;
};
// Regex that checks whether string contains only letters, numbers, hyphens, underscores, or periods.
const INPUT_REGEX = /^[\w0-9._-]+$/i;

/**
 *
 * This dialog handles creating a new parser.
 *
 */
const CustomParserDialog: FC<CustomParserDialogProps> = ({
  newParserDialogIsOpen,
  setNewParserDialogIsOpen,
  onModifyParser,
}: CustomParserDialogProps) => {
  const [input, setInput] = useState<string>('');
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const inputIsValid = INPUT_REGEX.test(input);

  const onDialogSubmit = (): void => {
    if (inputIsValid) {
      onModifyParser(input);
    }
  };

  const onInputChange = (e: SyntheticEvent): void => {
    setInput((e?.target as HTMLTextAreaElement)?.value);
    if (!isDirty) {
      setIsDirty(true);
    }
  };

  const onClose = (): void => {
    setNewParserDialogIsOpen(false);
    setInput('');
    setIsDirty(false);
  };

  return (
    <Dialog
      title="Create Parser"
      className="ParsersPage__Modal"
      show={newParserDialogIsOpen}
      close={onClose}
      okCallback={onDialogSubmit}
      onEnterCallback={onDialogSubmit}
      okButtonDisabled={!inputIsValid}
      testId={ELEMENT.createParserDialog}
    >
      <div>
        <p>
          {`Before creating a parser, we recommend configuring the Scalyr Agent (or some other data source) to upload at
          least one log file using that format. Once you've done so, you should see the parser listed on this page, and
          you can simply click the "Create" next to that parser. This will allow you to test your parser on sample data
          as you build it. You'll also then see an "Ask Us To Create For You" button, which will allow Scalyr staff to
          build you a parser`}
        </p>
        <br />
        <p>
          For instructions on configuring the Scalyr Agent, see
          <Link
            href={HELP_ROUTE.HELP_INSTALL_AGENT_LINUX_QUICK_START_1}
            className="link"
            openInNewTab
            testId={ELEMENT.agentInstallationLink}
          >
            {' Agent Installation '}
          </Link>
          or
          <Link
            href={HELP_ROUTE.SOLUTIONS_UPLOAD_MULTIPLE_LOGS}
            className="link"
            openInNewTab
            testId={ELEMENT.addNewLogFilesLink}
          >
            {'  Adding New Log Files.'}
          </Link>
        </p>
        <br />
        <p>
          If you&apos;d prefer to create a parser without first uploading a log file, simply enter the parser name
          below.
        </p>
      </div>
      <br />
      <Input
        isInvalid={isDirty && !inputIsValid}
        value={input}
        onChange={onInputChange}
        placeholder="Enter the name of your parser"
        testId={ELEMENT.newParserInput}
      />
      {isDirty && !inputIsValid && (
        <p className="ParsersPage__ErrorMessage">
          This parser name is invalid. Please use only letters, numbers, hyphens, underscores, or periods.
        </p>
      )}
    </Dialog>
  );
};

export default CustomParserDialog;
